/**
 * Login/Registration Page for Project Adya
 * Distinctive warm professional aesthetic with smooth animations
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    clearError();
    setEmail('');
    setPassword('');
  };

  return (
    <div className="min-h-screen flex">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400;1,9..40,500&display=swap');

        :root {
          --terracotta: #0066CC;
          --terracotta-dark: #003D7A;
          --terracotta-light: #4A9FE6;
          --cream: #F5F7FA;
          --warm-white: #FFFFFF;
          --charcoal: #2C3E50;
          --charcoal-light: #5A6C7D;
          --sand: #E5E9ED;
        }

        .login-container {
          font-family: 'DM Sans', -apple-system, sans-serif;
          color: var(--charcoal);
        }

        .login-title {
          font-family: 'Crimson Text', Georgia, serif;
          font-weight: 600;
          font-size: 3rem;
          line-height: 1.1;
          color: var(--charcoal);
          letter-spacing: -0.02em;
        }

        .login-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 1.125rem;
          font-weight: 400;
          color: var(--charcoal-light);
          line-height: 1.6;
        }

        .login-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 1.5px solid var(--sand);
          border-radius: 8px;
          background: var(--warm-white);
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          color: var(--charcoal);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          outline: none;
        }

        .login-input:focus {
          border-color: var(--terracotta);
          box-shadow: 0 0 0 3px rgba(200, 88, 53, 0.1);
        }

        .login-input::placeholder {
          color: #9B938C;
        }

        .login-button {
          width: 100%;
          padding: 0.875rem 1.5rem;
          background: var(--terracotta);
          color: white;
          border: none;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 12px rgba(200, 88, 53, 0.2);
        }

        .login-button:hover:not(:disabled) {
          background: var(--terracotta-dark);
          transform: translateY(-1px);
          box-shadow: 0 4px 20px rgba(200, 88, 53, 0.3);
        }

        .login-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .toggle-link {
          color: var(--terracotta);
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          transition: color 0.2s;
          border-bottom: 1px solid transparent;
        }

        .toggle-link:hover {
          color: var(--terracotta-dark);
          border-bottom-color: var(--terracotta-dark);
        }

        .error-message {
          background: #FEF2F2;
          border: 1px solid #FCA5A5;
          color: #991B1B;
          padding: 0.75rem 1rem;
          border-radius: 6px;
          font-size: 0.875rem;
          animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .form-fade-in {
          animation: fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .decorative-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.15;
          pointer-events: none;
        }
      `}</style>

      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'var(--cream)' }}>
        <div className="decorative-blob" style={{
          width: '400px',
          height: '400px',
          background: 'var(--terracotta)',
          top: '-100px',
          right: '-100px',
        }} />
        <div className="decorative-blob" style={{
          width: '300px',
          height: '300px',
          background: 'var(--terracotta-light)',
          bottom: '-50px',
          left: '-50px',
        }} />

        <div className="relative z-10 flex flex-col justify-center px-16 py-24">
          <div className="space-y-6">
            <div className="inline-block">
              <h1 className="login-title" style={{ fontSize: '4rem', marginBottom: '0.5rem' }}>
                Adya
              </h1>
              <div style={{
                height: '3px',
                background: 'var(--terracotta)',
                width: '80px',
                borderRadius: '2px',
              }} />
            </div>

            <p className="login-subtitle" style={{ maxWidth: '480px', fontSize: '1.25rem' }}>
              Intelligent project management for consulting firms.
              Transform client conversations into comprehensive proposals.
            </p>

            <div className="pt-8 space-y-4" style={{ color: 'var(--charcoal-light)', fontSize: '1rem' }}>
              <div className="flex items-start gap-3">
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'var(--terracotta)',
                  opacity: 0.2,
                  flexShrink: 0,
                }} />
                <div>
                  <strong>AI-Powered Proposals</strong>
                  <p style={{ opacity: 0.7, marginTop: '0.25rem' }}>
                    Generate detailed project proposals from meeting transcripts
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'var(--terracotta)',
                  opacity: 0.2,
                  flexShrink: 0,
                }} />
                <div>
                  <strong>Multi-Tenant Architecture</strong>
                  <p style={{ opacity: 0.7, marginTop: '0.25rem' }}>
                    Manage multiple clients and projects in one place
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'var(--terracotta)',
                  opacity: 0.2,
                  flexShrink: 0,
                }} />
                <div>
                  <strong>Conversational Refinement</strong>
                  <p style={{ opacity: 0.7, marginTop: '0.25rem' }}>
                    Iterate on proposals through natural dialogue
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 login-container" style={{ background: 'var(--warm-white)' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <h1 className="login-title" style={{ fontSize: '2.5rem' }}>Adya</h1>
            <div style={{
              height: '2px',
              background: 'var(--terracotta)',
              width: '60px',
              borderRadius: '2px',
              marginTop: '0.5rem',
            }} />
          </div>

          <div className="form-fade-in">
            <div className="mb-8">
              <h2 className="login-title" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                Welcome back
              </h2>
              <p className="login-subtitle">
                Sign in to access your projects and proposals
              </p>
            </div>

            {error && (
              <div className="error-message mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  className="login-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                  Password
                </label>
                <input
                  type="password"
                  className="login-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                className="login-button mt-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center" style={{ fontSize: '0.875rem', color: 'var(--charcoal-light)' }}>
              <button type="button" className="toggle-link" onClick={resetForm}>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
